import { format } from 'date-fns';
import React from 'react';

import { LiftStatusStorage } from 'Utils/Storage';
import { nl } from 'date-fns/locale';

export enum EGlobalStateCases {
  setDateRange = 'SET_DATE_RANGE',
  setDatesLocal = 'SET_DATES_LOCAL',
  setLockedLiftId = 'SET_LOCKED_LIFT_ID',
  removedLockedLiftId = 'REMOVE_LOCKED_LIFT_ID',
  switchViewToCurrent = 'SWITCH_VIEW_TO_CURRENT',
  setSenseNotConnectedFilter = 'SET_SENSE_NOT_CONNECTED_FILTER',
}

interface IGlobalState {
  locale: string;
  userToken: string | undefined;
  viewIsCurrent: boolean;
  lockedLiftId: string | undefined;
  senseNotConnectedFilter: string;
  setDates: (payload: { startDate: Date; endDate: Date }) => void;
}

type SetDateRangeAction = {
  type: EGlobalStateCases.setDateRange;
  payload: { startDate: Date; endDate: Date };
};

type SetDatesLocalAction = {
  type: EGlobalStateCases.setDatesLocal;
  payload: { startDate: Date; endDate: Date };
};

type SetLockedLiftIdAction = {
  type: EGlobalStateCases.setLockedLiftId;
  payload: string;
};

type RemoveLockedLiftIdAction = {
  type: EGlobalStateCases.removedLockedLiftId;
};

type SwitchViewToCurrentAction = {
  type: EGlobalStateCases.switchViewToCurrent;
  payload: boolean;
};

type SetSenseNotConnectedFilterAction = {
  type: EGlobalStateCases.setSenseNotConnectedFilter;
  payload: string;
};

type IGlobalStateAction =
  | SetDateRangeAction
  | SetDatesLocalAction
  | SetLockedLiftIdAction
  | RemoveLockedLiftIdAction
  | SwitchViewToCurrentAction
  | SetSenseNotConnectedFilterAction;

const GlobalStateContext = React.createContext<
  [IGlobalState, React.Dispatch<IGlobalStateAction>] | null
>(null);

const setDates = (payload: { startDate: Date; endDate: Date }) => {
  LiftStatusStorage.startDate = format(
    new Date(payload.startDate),
    'yyyy-MM-dd',
    {
      locale: nl,
    }
  );
  LiftStatusStorage.endDate = format(new Date(payload.endDate), 'yyyy-MM-dd', {
    locale: nl,
  });
};

const INITIAL_STATE: IGlobalState = {
  locale: 'NL-nl',
  userToken: undefined,
  viewIsCurrent: false,
  lockedLiftId: undefined,
  senseNotConnectedFilter: '',
  setDates,
};

const reducer = (
  state: IGlobalState,
  action: IGlobalStateAction
): IGlobalState => {
  switch (action.type) {
    case EGlobalStateCases.setDateRange:
      return {
        ...state,
      };
    case EGlobalStateCases.setDatesLocal:
      if (
        action.payload &&
        typeof action.payload === 'object' &&
        'startDate' in action.payload &&
        'endDate' in action.payload
      ) {
        setDates(action.payload as { startDate: Date; endDate: Date });
      }
      return {
        ...state,
      };
    case EGlobalStateCases.setLockedLiftId:
      LiftStatusStorage.lockedLift = action.payload as string;
      return {
        ...state,
        lockedLiftId: action.payload,
      };
    case EGlobalStateCases.removedLockedLiftId:
      LiftStatusStorage.removeLockedLift();
      return {
        ...state,
        lockedLiftId: undefined,
      };
    case EGlobalStateCases.switchViewToCurrent:
      localStorage.setItem('viewIsCurrent', String(action.payload));
      return {
        ...state,
        viewIsCurrent: action.payload,
      };
    case EGlobalStateCases.setSenseNotConnectedFilter:
      localStorage.setItem('senseNotConnectedFilter', action.payload);
      return {
        ...state,
        senseNotConnectedFilter: action.payload,
      };
    default: {
      return state;
    }
  }
};

const GlobalStateProvider = ({
  userToken,
  children,
}: {
  userToken: string | null;
  children: React.ReactNode;
}) => {
  const [globalState, globalStateDispatch] = React.useReducer(reducer, {
    ...INITIAL_STATE,
    userToken: userToken || '',
    lockedLiftId: LiftStatusStorage.lockedLiftId ?? undefined,
    viewIsCurrent: localStorage.getItem('viewIsCurrent') === 'true',
  });

  return (
    <GlobalStateContext.Provider
      value={[
        {
          ...globalState,
          setDates,
        },
        globalStateDispatch,
      ]}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

const useGlobalState = () => {
  const globalState = React.useContext(GlobalStateContext);

  if (!globalState) {
    throw new Error(
      'useGlobalState must be used withing a GlobalStateProvider'
    );
  }

  return globalState;
};

export default GlobalStateProvider;
export { useGlobalState };
